import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import Confetti from "react-confetti";
import { useWindowSize } from "@/hooks/useWindowSize";
import golfBallSrc from "@/assets/images/golf-ball-blue.png";
import { sysColors } from "@/assets/theme/colors";

export type ConfettiVariant = "pgalogo" | "golf" | "default";

interface PgaConfettiProps {
  run?: boolean;
  recycle?: boolean;
  numberOfPieces?: number;
  wind?: number;
  gravity?: number;
  opacity?: number;
  onConfettiComplete?: () => void;
  initialVelocityX?: number;
  initialVelocityY?: number;
  friction?: number;
  colors?: string[];
  variant?: ConfettiVariant;
  tweenDuration?: number;
  minInitialVelocityX?: number;
}

// Define variant-specific configuration
interface VariantConfig {
  imageLoader: () => Promise<HTMLImageElement | HTMLImageElement[]>;
  drawShape: ((ctx: CanvasRenderingContext2D) => void) | undefined;
  confettiSource: (windowSize: { width: number; height: number }) => {
    x: number;
    y: number;
    w: number;
    h: number;
  };
  physics: {
    gravity: number;
    initialVelocityY: number;
    friction: number;
    initialVelocityX: number;
  };
}

// helper to keep |vx| above a threshold
const getRandomVelocityXWithMinimum = (min: number, max: number) => {
  const magnitude = Math.random() * (max - min) + min;
  return Math.random() < 0.5 ? -magnitude : magnitude;
};

const PgaConfetti: React.FC<PgaConfettiProps> = ({
  run = true,
  recycle = true,
  numberOfPieces = 200,
  wind = 0,
  gravity = 0.1,
  opacity = 1.0,
  onConfettiComplete,
  initialVelocityX = 0,
  initialVelocityY = 0,
  friction = 0.97, //
  colors = ["#000"],
  variant = "default",
  tweenDuration = 0,
  minInitialVelocityX = 25
}) => {
  const windowSize = useWindowSize();
  const [iconImages, setIconImages] = useState<HTMLImageElement[]>([]);
  const icons = useMemo(() => [golfBallSrc], []);
  const imagesLoadedRef = useRef<boolean>(false);

  // Store a mapping of particles to their assigned icon indices
  // This will persist across renders and maintain consistency
  const particleIconMap = useRef(new Map<object, { index: number; rotation: number }>());

  // Properly load image with Promise
  const loadImage = useCallback((src: string): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e);
      img.src = src;
    });
  }, []);

  // Load all icons for variety
  const loadIcons = useCallback(async (): Promise<HTMLImageElement[]> => {
    try {
      // Load all icons in parallel
      const imagePromises = icons.map((icon) => loadImage(icon));
      const results = await Promise.all(imagePromises);

      setIconImages(results);

      return results;
    } catch (error) {
      console.error("Failed to load icons:", error);
      return []; // Return empty array on error
    }
  }, [loadImage, icons]);

  // Helper function for fallback shapes
  const drawFallbackShape = useCallback((ctx: CanvasRenderingContext2D) => {
    ctx.beginPath();
    ctx.arc(0, 0, 8, 0, 2 * Math.PI);
    ctx.fillStyle = sysColors["sys-brand-secondary"] || "#006747";
    ctx.fill();
  }, []);

  // Draw a random icon from our loaded images
  const drawRandomIcon = useCallback(
    function (this: any, ctx: CanvasRenderingContext2D) {
      if (iconImages.length === 0) {
        drawFallbackShape(ctx);
        return;
      }

      // Check if this particle already has an assigned icon index and rotation
      if (!particleIconMap.current.has(this)) {
        // Assign a truly random icon index and rotation to this particle
        const randomIndex = Math.floor(Math.random() * iconImages.length);
        const randomRotation = Math.random() * Math.PI * 2; // Random rotation 0-360 degrees
        particleIconMap.current.set(this, { index: randomIndex, rotation: randomRotation });
      }

      // Get the assigned icon index and rotation
      const particleData = particleIconMap.current.get(this) || { index: 0, rotation: 0 };
      const selectedImage = iconImages[particleData.index];

      if (selectedImage && selectedImage.complete) {
        // Enable image smoothing for better scaling quality
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";

        const scale = 0.4;
        const width = selectedImage.width * scale;
        const height = selectedImage.height * scale;

        // Apply rotation
        ctx.save();
        ctx.rotate(particleData.rotation);

        // Draw the image with proper centering and scaling
        ctx.drawImage(selectedImage, -width / 2, -height / 2, width, height);

        // Restore the context
        ctx.restore();
      } else {
        drawFallbackShape(ctx);
      }
    },
    [iconImages, drawFallbackShape]
  );

  // Source configurations
  const topSource = useCallback(
    (size: { width: number; height: number }) => ({
      x: 0,
      y: 0,
      w: size.width,
      h: 5 // Thin strip at the top
    }),
    []
  );

  const bottomSource = useCallback(
    (size: { width: number; height: number }) => ({
      x: (size.width / 16) * 8,
      y: size.height * 0.9 - 10,
      w: size.width / 8,
      h: 0 // Thin strip at the bottom
    }),
    []
  );

  // Variant configurations wrapped in useMemo
  const variantConfigs = useMemo(() => {
    return {
      pgalogo: {
        imageLoader: loadIcons,
        drawShape: iconImages.length > 0 ? drawRandomIcon : undefined,
        confettiSource: topSource,
        physics: {
          gravity,
          initialVelocityY,
          friction,
          initialVelocityX:
            Math.abs(initialVelocityX) < minInitialVelocityX
              ? getRandomVelocityXWithMinimum(minInitialVelocityX, 25)
              : initialVelocityX
        }
      },
      golf: {
        imageLoader: loadIcons,
        drawShape: iconImages.length > 0 ? drawRandomIcon : undefined,
        confettiSource: bottomSource,
        physics: {
          gravity: 0.2,
          initialVelocityY: Math.random() * 40 + 35,
          friction: 0.95,
          initialVelocityX: (() => {
            const vx = Math.random() * 40 + Math.random() * 5;
            return Math.abs(vx) < minInitialVelocityX ? getRandomVelocityXWithMinimum(minInitialVelocityX, 45) : vx;
          })()
        }
      },
      default: {
        confettiSource: topSource,
        physics: {
          gravity,
          initialVelocityY,
          friction,
          initialVelocityX:
            Math.abs(initialVelocityX) < minInitialVelocityX
              ? getRandomVelocityXWithMinimum(minInitialVelocityX, 25)
              : initialVelocityX
        }
      }
    } as Record<ConfettiVariant, Partial<VariantConfig>>;
  }, [
    iconImages,
    gravity,
    initialVelocityY,
    friction,
    drawRandomIcon,
    loadIcons,
    topSource,
    bottomSource,
    minInitialVelocityX,
    initialVelocityX
  ]);

  // Clear the particle-icon mapping when the run state changes
  useEffect(() => {
    if (!run) {
      particleIconMap.current.clear();
    }
  }, [run]);

  // Load appropriate image based on variant
  useEffect(() => {
    if (imagesLoadedRef.current) return;

    const loadImages = async () => {
      const config = variantConfigs[variant];
      if (config.imageLoader) {
        try {
          await config.imageLoader();
          imagesLoadedRef.current = true;
        } catch (error) {
          console.error("Error loading images:", error);
        }
      }
    };

    loadImages();

    return () => {
      // Clean up
    };
  }, [variant, variantConfigs]);

  // Get current variant config
  const currentConfig = variantConfigs[variant];

  const confettiStyle = {
    zIndex: 9999, // confetti over everything
    pointerEvents: "none" as "none" // don't block ui
  };

  return (
    <Confetti
      style={confettiStyle}
      width={windowSize.width}
      height={windowSize.height}
      run={run}
      recycle={recycle}
      numberOfPieces={numberOfPieces}
      drawShape={currentConfig.drawShape}
      wind={wind}
      gravity={currentConfig.physics?.gravity ?? gravity}
      opacity={opacity}
      onConfettiComplete={onConfettiComplete}
      confettiSource={currentConfig.confettiSource?.(windowSize)}
      initialVelocityY={currentConfig.physics?.initialVelocityY ?? initialVelocityY}
      initialVelocityX={currentConfig.physics?.initialVelocityX ?? initialVelocityX}
      friction={currentConfig.physics?.friction ?? friction}
      colors={colors}
      tweenDuration={tweenDuration}
    />
  );
};

export default PgaConfetti;
